import { Paper, Switch, Table, TableCell, TableHead, TableRow } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ReorderIcon from '@material-ui/icons/Reorder';
import arrayMove from 'array-move';
import React, { Fragment } from 'react';
import { SortableContainer as sortableContainer, SortableElement as sortableElement, SortableHandle as sortableHandle } from 'react-sortable-hoc';
import { Help } from '../../components/Help';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import { ITheme } from '../../services/types';

interface IProps {
  popularThemes?: ITheme[];
  restThemes?: ITheme[];
  isEditing?: boolean;
  onPopularToggle: (theme: ITheme) => void;
  onPopularOrderChange: (themes: ITheme[]) => void;
}

const useStyles = makeStyles(() => ({
  dragHelper: {
    display: 'table',
  },
}));

const SortableTable = sortableContainer((props: any) => {
  const { children } = props;

  return <Table>{children}</Table>;
});

const SortableTableRow = sortableElement(TableRow);
const DragHandle = sortableHandle(() => <ReorderIcon style={{ color: 'gray' }} />);

const ThemeList = (props: IProps): JSX.Element => {
  const { popularThemes = [], restThemes = [], isEditing = false, onPopularToggle, onPopularOrderChange } = props;
  const confirm = useConfirmDialog();

  const classes = useStyles();

  const setThemePopular = (theme: ITheme) => {
    confirm.open({
      onProceed: () => onPopularToggle(theme),
    });
  };

  const handleSortEnd = ({ newIndex, oldIndex }: { newIndex: number; oldIndex: number }) => {
    if (!popularThemes) {
      return;
    }

    const sorted = arrayMove([...popularThemes], oldIndex - 1, newIndex - 1);
    sorted.forEach((t, i) => (t.order = i + 1));

    onPopularOrderChange(sorted);
  };

  const renderCommonTableRow = (theme: ITheme) => (
    <Fragment>
      <TableCell align="center" style={{ width: 60 }}>
        {theme.id}
      </TableCell>
      <TableCell>{theme.category}</TableCell>
      <TableCell>{theme.title}</TableCell>
      <TableCell align="center">
        <Switch
          color="primary"
          checked={theme.isPopular}
          onClick={e => e.stopPropagation()}
          onChange={() => setThemePopular(theme)}
          disabled={isEditing}
        />
      </TableCell>
    </Fragment>
  );

  return (
    <Paper>
      <SortableTable axis="y" lockAxis="y" useDragHandle={true} transitionDuration={0} helperClass={classes.dragHelper} onSortEnd={handleSortEnd}>
        <TableHead>
          {isEditing && <TableCell align="center" style={{ width: 30 }}></TableCell>}
          <TableCell align="center" style={{ width: 60 }}>
            Id
          </TableCell>
          <TableCell>Category</TableCell>
          <TableCell>Title</TableCell>
          <TableCell align="center">
            Popular?
            <Help text="When designated as popular themes, it is exposed at the top of Search > Browse by theme > Theme list and at the bottom of home." />
          </TableCell>
        </TableHead>
        {popularThemes.map(theme => (
          <SortableTableRow index={theme.order} key={theme.id} style={{ width: '100%' }}>
            {isEditing && (
              <TableCell align="center" style={{ lineHeight: 0, width: 30 }}>
                <DragHandle />
              </TableCell>
            )}
            {renderCommonTableRow(theme)}
          </SortableTableRow>
        ))}
        {restThemes.map(theme => (
          <TableRow key={theme.id}>
            {isEditing && <TableCell align="center" style={{ width: 60 }}></TableCell>}
            {renderCommonTableRow(theme)}
          </TableRow>
        ))}
      </SortableTable>
    </Paper>
  );
};

export default ThemeList;
